import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, ChevronRight, Send, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import Layout from '../components/layout/Layout';
import { contactInfo, doctorInfo } from '../data/mock';
import { submitContactForm } from '../services/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      await submitContactForm(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(
        error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 medical-pattern opacity-50" />
        <div className="decorative-circle w-96 h-96 -top-48 -right-48 opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full text-teal-700 text-sm font-medium mb-6">
              <Calendar className="w-4 h-4" />
              <span>Prendre rendez-vous</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-light text-gray-900">
              Contact et parcours patient
            </h1>
            <p className="mt-6 text-xl text-gray-600 leading-relaxed">
              Toutes les informations pratiques pour préparer votre consultation et comprendre les étapes de votre prise en charge.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Address Card */}
            <Card className="border-0 shadow-lg hover-lift overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-teal-400 to-teal-600" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-gray-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-teal-700" />
                  </div>
                  {contactInfo.consultation.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 whitespace-pre-line mb-4">
                  {contactInfo.consultation.address}
                </p>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-500 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                    {contactInfo.consultation.access}
                  </p>
                  <p className="text-gray-500 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                    {contactInfo.consultation.parking}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Hours Card */}
            <Card className="border-0 shadow-lg hover-lift overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-teal-400 to-teal-600" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-gray-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-teal-700" />
                  </div>
                  {contactInfo.hours.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {contactInfo.hours.schedule.map((item, index) => (
                    <li key={index} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                      <span className="text-gray-600">{item.day}</span>
                      <span className="text-gray-900 font-medium">{item.hours}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card className="border-0 shadow-lg hover-lift overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-teal-400 to-teal-600" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-gray-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-teal-700" />
                  </div>
                  Nous contacter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <a
                    href={`tel:${doctorInfo.phone}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-teal-50 transition-colors group"
                  >
                    <Phone className="w-4 h-4 text-gray-400 group-hover:text-teal-600" />
                    <span className="text-gray-700 group-hover:text-teal-700 font-medium">{doctorInfo.phone}</span>
                  </a>
                  <a
                    href={`mailto:${doctorInfo.email}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-teal-50 transition-colors group"
                  >
                    <Mail className="w-4 h-4 text-gray-400 group-hover:text-teal-600" />
                    <span className="text-gray-700 group-hover:text-teal-700 font-medium">{doctorInfo.email}</span>
                  </a>
                </div>
                <p className="text-xs text-gray-500 mt-4 p-3 bg-amber-50 rounded-lg">
                  Prise de rendez-vous par téléphone uniquement
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      {/* Contact Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-medium text-gray-900 mb-2">
                Formulaire de contact
              </h2>
              <p className="text-gray-600">
                Pour toute question ou demande d'information, vous pouvez nous écrire via ce formulaire.
              </p>
            </div>

            {submitStatus === 'success' && (
              <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100 mb-8 animate-fade-in-up">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-teal-800">Message envoyé avec succès</p>
                    <p className="text-sm text-teal-700">Nous vous répondrons dans les meilleurs délais.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {submitStatus === 'error' && (
              <Card className="border-0 shadow-lg bg-red-50 mb-8 animate-fade-in-up">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-red-800">Erreur lors de l'envoi</p>
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-700">Nom complet *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        minLength={2}
                        placeholder="Jean Dupont"
                        className="h-12 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="jean.dupont@example.com"
                        className="h-12 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700">Téléphone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        className="h-12 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-gray-700">Sujet *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        minLength={5}
                        placeholder="Demande de rendez-vous"
                        className="h-12 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-700">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={10}
                      rows={5}
                      placeholder="Décrivez brièvement votre demande..."
                      className="border-gray-200 focus:border-teal-500 focus:ring-teal-500 resize-none"
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-teal-700 hover:bg-teal-800 text-white px-8 h-12 shadow-lg shadow-teal-700/20 hover:shadow-xl hover:shadow-teal-700/30 transition-all"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Envoi en cours...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Envoyer
                          <Send className="w-4 h-4" />
                        </span>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 text-center pt-4 border-t border-gray-100">
                    Les champs marqués d'un * sont obligatoires. Vos données sont traitées conformément au RGPD.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      {/* Patient Journey */}
      <section className="py-16 bg-gray-50 medical-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-medium text-gray-900 mb-10 text-center">
            {contactInfo.process.title}
          </h2>
          <div className="relative">
            {/* Connection line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-200 via-teal-400 to-teal-200 -translate-y-1/2" />
            
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {contactInfo.process.steps.map((step, index) => (
                <div key={step.step} className="relative">
                  <Card className="border-0 shadow-lg h-full bg-white hover-lift">
                    <CardContent className="pt-6 pb-6 text-center">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-600 to-teal-700 text-white flex items-center justify-center font-semibold mx-auto mb-4 shadow-lg shadow-teal-600/30 relative z-10">
                        {step.step}
                      </div>
                      <h3 className="font-medium text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Practical Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-medium text-gray-900 mb-6 text-center">
              Informations pratiques
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg hover-lift">
                <CardContent className="p-6">
                  <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-teal-600" />
                    </span>
                    Préparer votre consultation
                  </h3>
                  <ul className="space-y-3 text-gray-600 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 flex-shrink-0" />
                      <span>Apportez votre carte vitale et votre mutuelle</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 flex-shrink-0" />
                      <span>Préparez la liste de vos traitements en cours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 flex-shrink-0" />
                      <span>Apportez vos examens récents (imagerie, biologie)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 flex-shrink-0" />
                      <span>Notez vos questions à l'avance si nécessaire</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg hover-lift">
                <CardContent className="p-6">
                  <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </span>
                    Délais de rendez-vous
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Les délais varient selon l'urgence de votre situation. En cas de symptômes urgents, veuillez le préciser lors de votre appel.
                  </p>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-800">
                      <strong>Urgence :</strong> Contactez le 15 (SAMU) ou rendez-vous aux urgences les plus proches.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
